import React, { useEffect, useState } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory, useParams } from "react-router";
import { StatusCode } from "api/protocols";

import { displayCurrency } from "helpers/Helpers";
import { IUIActions } from "redux/actions/ui";

import { ButtonNew as Button } from "components/buttonNew";

import { ICart } from "redux/reducers/customer/models/cart";
import { ICartState } from "redux/reducers/customer/cart";
import { ICartActions } from "redux/actions/customer/cart";
import { getFoodOrderingCartToken, getFoodOrderingCartTokenName } from "./Menu";
import { GetFacility } from "api/rpc/guest/facility";
import { GetOrder } from "api/rpc/2024-04/guest/order";
import { IFacility } from "pages/guest/models/facility";
import { IOrder, IOrderLineItem, IOrderTaxLine } from "pages/guest/models/order";
import "./clientPortalFoodOrderingConfirmation.scss";
import { useTranslation } from "react-i18next";

interface IParams {
  clientShortName: string;
  facilityShortName: string;
  orderToken: string;
}

interface IConfirmationProps {
  cartStore: ICartState;
  cartActions: ICartActions;
  uiActions: IUIActions;
}

interface IConfirmationState {
  facility: IFacility;
  order: IOrder;
}

export default function FoodOrderingCartConfirmation(props: IConfirmationProps) {
  const { clientShortName, facilityShortName, orderToken } = useParams<IParams>();
  const { cartStore, cartActions, uiActions } = props;
  const history = useHistory();
  const { t } = useTranslation();
  const [confirmationState, setConfirmationState] = useState<IConfirmationState>({
    facility: null,
    order: null,
  });

  useEffect(() => {
    void loadOrder();
  }, []);

  async function loadOrder() {
    uiActions.enqueue();

    try {
      const facilityRes = await GetFacility({ short_name: facilityShortName }, true);
      const facilityId = facilityRes?.data?.[0]?.id;

      if (facilityRes.status !== StatusCode.OK || facilityId === undefined) {
        // uiActions.showError(t("Failed to load facility"));
        uiActions.dequeue();
        return;
      }

      let cart: ICart = undefined;

      if (getFoodOrderingCartToken(facilityShortName)) {
        try {
          const getCartPromise: Promise<ICart> = cartStore?.isLoaded
            ? Promise.resolve(cartStore?.cart)
            : (cartActions.loadCart(
                {
                  facility_id: facilityId,
                  tokenName: getFoodOrderingCartTokenName(facilityShortName),
                  token: getFoodOrderingCartToken(facilityShortName),
                },
                true,
              ) as unknown as Promise<ICart>);

          cart = await getCartPromise;
        } catch {
          //   uiActions.showError(t("Failed to load cart"));
          uiActions.dequeue();
          return;
        }
      }

      if (cart?.status === "complete") {
        cartActions.cartClear({ tokenName: getFoodOrderingCartTokenName(facilityShortName) });
      }

      const getOrderResponse = await GetOrder({ token: orderToken }, true);

      if (getOrderResponse.status !== StatusCode.OK) {
        // uiActions.showError(t("Failed to load order"));
        uiActions.dequeue();
        return;
      }

      const facility = facilityRes.data?.[0];
      const order = getOrderResponse.data?.[0];

      setConfirmationState(prevState => ({ ...prevState, facility: facility, order: order }));

      uiActions.dequeue();
    } catch {
      uiActions.dequeue();
    }
  }

  function sendToHome() {
    history.push(`/portal/${clientShortName}/food-ordering/menu`);
  }

  return (
    <div className="food-ordering-confirmation-container">
      <div className="food-ordering-confirmation-container_close_icon_container">
        <FontAwesomeIcon
          size="1x"
          fixedWidth
          icon={["far", "xmark"]}
          className="food-ordering-confirmation-container_close_icon"
          onClick={sendToHome}
        />
      </div>

      <div className="food-ordering-confirmation-container_success_icon">
        <FontAwesomeIcon size="1x" fixedWidth icon={["far", "check-circle"]} />
      </div>

      <div>
        <p className="food-ordering-confirmation-container_success_title">
          {t("Your order has been placed successfully")}
        </p>
      </div>

      <div>
        <p className="food-ordering-confirmation-container_order_summary_title">
          {t("Order Summary")}
        </p>
        <p className="food-ordering-confirmation-container_order_info">{confirmationState?.facility?.long_name}</p>
        {confirmationState?.order?.processed_at && (
          <p className="food-ordering-confirmation-container_order_info">
            {moment(confirmationState?.order?.processed_at).format("dddd MMMM D, YYYY")} •{" "}
            {moment(confirmationState?.order?.processed_at).format("LT")}
          </p>
        )}
        <p className="food-ordering-confirmation-container_order_confirmation_number">
          Confirmation Number: {confirmationState?.order?.name}
        </p>
      </div>

      <div>
        <p className="food-ordering-confirmation-container_order_details_title">
          {t("Order Details")}
        </p>

        <p className="food-ordering-confirmation-container_order_customer_title">
          {t("Ordered By")}
        </p>
        <p className="food-ordering-confirmation-container_order_customer_info">
          {confirmationState?.order?.customer?.full_name}
        </p>
        <p className="food-ordering-confirmation-container_order_customer_info">
          {confirmationState?.order?.customer?.email}
        </p>
        <p className="food-ordering-confirmation-container_order_customer_info">
          {confirmationState?.order?.customer?.phone}
        </p>
      </div>

      <div>
        <p className="food-ordering-confirmation-container_order_due_at_course_title">
          {confirmationState?.order?.financial_status === "paid" ? t("Prepaid Details") : t("Due At Course")}
        </p>
      </div>

      <div>
        <p className="food-ordering-confirmation-container_order_items_title">
          {t("Items")}
        </p>

        {confirmationState?.order?.line_items.filter(item => !item.parent_id).map((item: IOrderLineItem, index: number) => {
          return (
            <>
              <div key={index} className="flex justify-between mt-2 mb-2 items-center">
                <div className="flex gap-4">
                  <p className="food-ordering-confirmation-container_order_item_quantity">{item.quantity}</p>
                  <p className="food-ordering-confirmation-container_order_item_title">{item.product_title}</p>
                </div>
                <div className="food-ordering-confirmation-container_line_item_separator">
                  ------------------------------------------------------------------------------------------------------------------------------------------------------
                </div>

                <div>
                  <p className="food-ordering-confirmation-container_order_item_price">
                    {displayCurrency("cad", item.subtotal_price)}
                  </p>
                </div>
              </div>
              {confirmationState?.order?.line_items.filter(modifier => modifier.parent_id === item.id).map((modifier: IOrderLineItem, index: number) => {
                return (
                  <div key={index} className="flex justify-between mt-2 mb-2 ml-4 items-center">
                    <div className="flex gap-4">
                      <p className="food-ordering-confirmation-container_order_item_quantity">{modifier.quantity}</p>
                      <p className="food-ordering-confirmation-container_order_item_title">{modifier.product_title}</p>
                    </div>
                    <div className="food-ordering-confirmation-container_line_item_separator">
                      ------------------------------------------------------------------------------------------------------------------------------------------------------
                    </div>

                    <div>
                      <p className="food-ordering-confirmation-container_order_item_price">
                        {displayCurrency("cad", modifier.subtotal_price)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </>
          );
        })}
      </div>

      <div>
        <div className="food-ordering-confirmation-container_order_subtotal_container">
          <p className="food-ordering-confirmation-container_order_subtotal_title">
            {t("Subtotal")}
          </p>
          <div className="food-ordering-confirmation-container_line_item_separator">
            ---------------------------------------------------------------------------------------------------------------------------------------------------------------
          </div>
          <p className="food-ordering-confirmation-container_order_subtotal_price">
            {displayCurrency("cad", confirmationState?.order?.subtotal_price)}
          </p>
        </div>

        <div>
          {confirmationState?.order?.tax_lines.map((taxLine: IOrderTaxLine, index: number) => {
            return (
              <div key={index} className="food-ordering-confirmation-container_order_tax_line_container">
                <p className="food-ordering-confirmation-container_order_tax_line_title">
                  {taxLine.title}{" "}
                  <span className="food-ordering-confirmation-container_order_tax_line_rate">
                    {taxLine.rate * 100}%
                  </span>
                </p>
                <div className="food-ordering-confirmation-container_line_item_separator">
                  ------------------------------------------------------------------------------------------------------------------------------------------------------
                </div>
                <p className="food-ordering-confirmation-container_order_tax_line_price">
                  {displayCurrency("cad", taxLine.price)}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="food-ordering-confirmation-container_order_total_price_container">
        <p className="food-ordering-confirmation-container_order_total_price_title">
          {t("Total")}
        </p>
        <p className="food-ordering-confirmation-container_order_total_price">
          {displayCurrency("cad", confirmationState?.order?.total_price)}
        </p>
      </div>

      <div className="food-ordering-confirmation-container_close_button_container">
        <Button block type="secondary" size="medium" onClick={sendToHome}>
          {t("Close")}
        </Button>
      </div>
    </div>
  );
}
